import React, { useState, useRef, useEffect } from "react";
import {
  useGetLearningResourcesQuery,
  useDeleteLearningResourceMutation,
} from "../../../services/api";
import LearningResourceForm from "./LearningResourceForm";
import ErrorMessage from "../../../Components/common/ErrorMessage";
import Loading from "../../../Components/common/Loading";
import "./AdminLearningLibrary.css";

// SVG Icons
const IconSearch = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconEdit = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconDelete = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconPlus = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconLink = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const IconFile = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// Resource type mapping
const RESOURCE_TYPES = [
  "Articles",
  "Videos",
  "Exercises",
  "Courses",
  "Workshops",
  "Other",
];

export default function AdminLearningLibrary() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const inputRef = useRef(null);

  const {
    data: resourcesData,
    isLoading,
    error,
    refetch,
  } = useGetLearningResourcesQuery({
    page,
    limit: 10,
    search: search || undefined,
    type: typeFilter || undefined,
  });

  const [deleteResource] = useDeleteLearningResourceMutation();

  // Keyboard shortcut Ctrl+K for search
  useEffect(() => {
    const onKey = (e) => {
      const isCtrlK = (e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === "k";
      if (isCtrlK) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCreateResource = () => {
    setEditingResource(null);
    setShowForm(true);
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingResource(null);
    refetch();
  };

  const handleDeleteResource = async (resourceId, resourceTitle) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${resourceTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteResource(resourceId).unwrap();
        refetch();
      } catch (err) {
        console.error("Failed to delete resource:", err);
        alert("Failed to delete resource. Please try again.");
      }
    }
  };

  const resources = resourcesData?.resources || [];
  const pagination = resourcesData?.pagination || {};

  if (isLoading && !resources.length) {
    return <Loading fullPage={false} />;
  }

  return (
    <div className="ll-admin-page">
      {/* Header */}
      <div className="ll-admin-header">
        <div>
          <h1 className="admin-h1">Learning Library</h1>
          <p className="ll-admin-subtitle">
            Manage learning resources for all users
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateResource}>
          <IconPlus /> Add Resource
        </button>
      </div>

      {/* Filters */}
      <div className="ll-admin-filters">
        <div className="ll-admin-count">
          Showing {resources.length} of {pagination.total || 0} resources
        </div>

        <div className="ll-admin-filter-controls">
          {/* Search */}
          <div className="ll-admin-search">
            <IconSearch />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search resources..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <span className="key-hint">Ctrl K</span>
          </div>

          {/* Type Filter */}
          <select
            className="ll-admin-select"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Types</option>
            {RESOURCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage error={error} />}

      {/* Resources Table */}
      <div className="ll-admin-table-container">
        <table className="ll-admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Source</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {resources.length === 0 ? (
              <tr>
                <td colSpan="5" className="ll-admin-empty">
                  No resources found
                </td>
              </tr>
            ) : (
              resources.map((resource) => (
                <tr key={resource._id}>
                  <td>
                    <div className="resource-title-cell">
                      <div className="resource-title">{resource.title}</div>
                      {resource.description && (
                        <div className="resource-description">
                          {resource.description.substring(0, 100)}
                          {resource.description.length > 100 ? "..." : ""}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`type-badge type-${resource.type?.toLowerCase()}`}>
                      {resource.type}
                    </span>
                  </td>
                  <td>
                    <div className="source-cell">
                      {resource.sourceType === "url" ? (
                        <>
                          <IconLink />
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="source-link"
                          >
                            External Link
                          </a>
                        </>
                      ) : (
                        <>
                          <IconFile />
                          <span className="source-file">
                            {resource.file?.filename || "Uploaded File"}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        resource.isActive ? "status-active" : "status-inactive"
                      }`}
                    >
                      {resource.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="ll-admin-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleEditResource(resource)}
                        title="Edit resource"
                      >
                        <IconEdit />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() =>
                          handleDeleteResource(resource._id, resource.title)
                        }
                        title="Delete resource"
                      >
                        <IconDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="ll-admin-pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= pagination.pages}
          >
            Next
          </button>
        </div>
      )}

      {/* Resource Form Modal */}
      {showForm && (
        <LearningResourceForm
          resource={editingResource}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
}
